import React, { useEffect, useState, useRef } from "react";
import { useUser } from "../../../context/UserContext";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import {
  formatBoldText,
  formatStreamingHtmlResponse,
  getCurrentSecionName,
} from "../../../services/commonService";
import {
  getChatWindowResponse,
  getDefaultChatQuestions,
  getLiveChatResponse,
} from "../../../services/accIqService";
import iq_api from "../../../services/iqApi";

export default function AIChat({ section = "" }) {
  const { user } = useUser();
  const { clientName, competitorName } = useParams();
  const [chat, setChat] = useState([]);
  const [input, setInput] = useState("");
  const [showQuestions, setShowQuestions] = useState(true);
  const [sessionId] = useState(() => uuidv4());
  // questions by section, each item includes {id, question, answer, sources, reasoning}
  const [questions, setQuestions] = useState({});
  const [isStreaming, setIsStreaming] = useState(false);
  const chatEndRef = useRef(null);

  function htmlToText(htmlString) {
    const doc = new DOMParser().parseFromString(htmlString, "text/html");
    return doc.body.textContent || "";
  }

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chat]); // runs whenever chat updates

  useEffect(() => {
    if (!section) return;

    const fetchQAData = async () => {
      try {
        const USER_COMPANY = clientName ?? "HB Fuller";
        const COMPANY_NAME = competitorName ?? "Tailored Chemical Products";
        // const table_name = section;
        // const table_name = "End User/Customer Segment Penetration";
        const table_name = getCurrentSecionName(section);

        const queryParams = new URLSearchParams({
          USER_COMPANY,
          COMPANY_NAME,
          table_name,
        }).toString();

        const response = await getChatWindowResponse(queryParams);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        const qna =
          data?.response?.[0]?.response?.competitive_intelligence_qna
            ?.questions || [];

        // console.log("QNA: " + JSON.stringify(qna));

        // store questions by section
        setQuestions((prev) => ({
          ...prev,
          [section]: qna,
        }));
      } catch (error) {
        console.error("Error fetching QA data:", error);
      }
    };

    fetchQAData();
  }, [section]);

  const addChat = (q, answerBlocks) => {
    // setChat((prev) => [
    //   ...prev,
    //   { type: "question", text: q },
    //   { type: "answer", blocks: answerBlocks },
    // ]);
    setChat((prev) => [
      ...prev,
      { id: uuidv4(), type: "question", text: q },
      { id: uuidv4(), type: "answer", from: "bot", blocks: answerBlocks },
    ]);
  };

  const handleQuestionClick = (qObj) => {
    setShowQuestions(false);

    // Add the question as a user message
    // setChat((prev) => [
    //   ...prev,
    //   { from: "user", type: "question", text: qObj.question },
    // ]);

    // // Then add the prebuilt answer as AI response
    // setChat((prev) => [...prev, { type: "answer", blocks: qObj.answer }]);
    setChat((prev) => [
      ...prev,
      { id: uuidv4(), from: "user", type: "question", text: qObj.question },
      { id: uuidv4(), from: "bot", type: "answer", blocks: qObj.answer },
    ]);
  };

  // const handleSend = async () => {
  //   if (!input.trim()) return;

  //   // Add user message to chat
  //   // setChat((prev) => [...prev, { from: "user", text: input }]);
  //   // Add user message
  //   setChat((prev) => [...prev, { id: uuidv4(), from: "user", text: input }]);

  //   setInput("");
  //   setIsStreaming(true);
  //   try {
  //     // const queryParams = new URLSearchParams({
  //     //   session_id: sessionId,
  //     //   message: input,
  //     //   user_input: input,
  //     // }).toString();
  //     const queryParams = {
  //       // session_id: sessionId,
  //       // message: input,
  //       user_input: input,
  //     };
  //     setInput("");

  //     const response = await getLiveChatResponse(queryParams);
  //     if (!response.ok) throw new Error("Failed to send message");

  //     // const data = await response.json();
  //     // setChat((prev) => [...prev, { from: "bot", text: data.answer }]);

  //     // since API returns streaming/plain text instead of JSON
  //     const text = await response.text();

  //     // append to chat
  //     // setChat((prev) => [...prev, { from: "bot", text, isSecondApi: true }]);
  //     // Append bot response
  //     setChat((prev) => [
  //       ...prev,
  //       { id: uuidv4(), from: "bot", text, isSecondApi: true },
  //     ]);
  //   } catch (err) {
  //     console.error("Error sending message:", err);
  //   }
  //   setIsStreaming(false);
  //   setInput(""); // clear input
  // };

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = {
      id: uuidv4(),
      from: "user",
      text: input,
    };

    const botMessageId = uuidv4(); // Unique ID for streamed message

    // Add user message + placeholder for bot
    setChat((prev) => [
      ...prev,
      userMessage,
      {
        id: botMessageId,
        from: "bot",
        text: "",
        isSecondApi: true,
      },
    ]);

    setInput("");
    setIsStreaming(true);

    try {
      const queryParams = {
        user_input: input,
      };

      const response = await getLiveChatResponse(queryParams);
      if (!response.ok || !response.body)
        throw new Error("Failed to stream response");

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let fullText = "";

      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process line by line
        const lines = buffer.split("\n");

        for (const line of lines) {
          const trimmed = line.trim();

          // Skip empty lines
          if (!trimmed) continue;

          // Done signal
          if (trimmed === "data: [DONE]") {
            buffer = "";
            break;
          }

          // Expect lines starting with "data:"
          if (trimmed.startsWith("data: ")) {
            const content = trimmed.replace("data: ", "");

            // Append new content
            fullText += content;

            // Update chat message
            setChat((prev) =>
              prev.map((msg) =>
                msg.id === botMessageId ? { ...msg, text: fullText } : msg
              )
            );
          }
        }

        // Clear buffer for next chunk
        buffer = lines[lines.length - 1].endsWith("data: [DONE]")
          ? ""
          : lines[lines.length - 1];
      }
    } catch (err) {
      console.error("Streaming error:", err);
      setChat((prev) => [
        ...prev,
        {
          id: uuidv4(),
          from: "bot",
          text: "An error occurred while getting a response.",
          isSecondApi: true,
        },
      ]);
    }

    setIsStreaming(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleSend();
  };

  return (
    <div className="ai_insight_wrapper">
      <div className="ai_insight_body">
        {/* Pre-Built Questions */}
        {showQuestions && questions[section]?.length > 0 && (
          <div className="ai_list_block">
            <h5 className="ai_list_head">Pre-built Analysis Questions:</h5>
            <ul className="ai_list_ul">
              {questions[section].map((qObj) => (
                <li
                  key={qObj.id}
                  className="ai_list_li"
                  onClick={() => handleQuestionClick(qObj)}
                  style={{ cursor: "pointer" }}
                >
                  <span className="mr-1">•</span> {qObj.question}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Chat Section */}
        <div className="chat_box_ai">
          {chat.map((item, index) =>
            item.from === "user" ? (
              <>
                <div key={item.id} className="system_ai_block" ref={chatEndRef}>
                  <h5 className="subname">{user?.firstname || "Mr.X"}</h5>

                  <div className="system_box_right">
                    {Array.isArray(item.text) ? (
                      item.text.map((msg, i) => (
                        <div key={i} className="system_text_right">
                          {msg}
                        </div>
                      ))
                    ) : (
                      <div className="system_text_right">{item.text}</div>
                    )}
                  </div>
                </div>
                <div className="clearfix"></div>
              </>
            ) : (
              <>
                <div key={item.id} className="user_block">
                  <h5 className="subname">AI Analysis</h5>
                  <div className="user_box_left">
                    <div className="insight_block">
                      <div className="row">
                        {/* Why Block */}
                        <div className="col-md-12" ref={chatEndRef}>
                          <div
                            className="card_block positive mb-2"
                            style={{ borderLeft: "1px solid #15803D29" }}
                          >
                            {/* Hide label if it's from second API */}
                            {!item.isSecondApi && (
                              <h6 className="head green_head">
                                Why is this happening?
                              </h6>
                            )}

                            <ul className="list_card_ul">
                              {item.blocks?.why?.length ? (
                                item.blocks.why.map((txt, i) => (
                                  <li key={i} className="list_card_li">
                                    <span
                                      className="list_card_text"
                                      style={
                                        item.isSecondApi
                                          ? { WebkitLineClamp: "unset" }
                                          : {}
                                      }
                                    >
                                      {formatBoldText(txt)}
                                    </span>
                                  </li>
                                ))
                              ) : (
                                // <span
                                //   key={item.key}
                                //   // ref={chatEndRef}
                                //   className="chat_response"
                                //   style={{
                                //     fontFamily: "Inter",
                                //     fontWeight: "400",
                                //     fontSize: "12px",
                                //     lineHeight: "17.4px",
                                //     marginBottom: "10px",
                                //     color: "#1e1c25",
                                //   }}
                                //   dangerouslySetInnerHTML={{
                                //     __html: item.text,
                                //   }}
                                // />
                                <span
                                  key={item.key}
                                  // ref={chatEndRef}
                                  className="chat_response"
                                  style={{
                                    fontFamily: "Inter",
                                    fontWeight: "400",
                                    fontSize: "12px",
                                    lineHeight: "17.4px",
                                    marginBottom: "10px",
                                    color: "#1e1c25",
                                  }}
                                  dangerouslySetInnerHTML={{
                                    __html: formatStreamingHtmlResponse(
                                      item.text
                                    ),
                                  }}
                                />
                              )}
                              {/* {item.blocks?.why?.length ? (
                                item.blocks.why.map((txt, i) => (
                                  <li key={i} className="list_card_li">
                                    <span
                                      className="list_card_text"
                                      style={
                                        item.isSecondApi
                                          ? { WebkitLineClamp: "unset" }
                                          : {}
                                      }
                                      dangerouslySetInnerHTML={{
                                        __html: formatBoldText(
                                          JSON.stringify(txt)
                                        ),
                                      }}
                                    />
                                  </li>
                                ))
                              ) : (
                                <span
                                  key={item.key}
                                  style={{
                                    fontFamily: "Inter",
                                    fontWeight: "400",
                                    fontSize: "12px",
                                    lineHeight: "17.4px",
                                    marginBottom: "10px",
                                    color: "#1e1c25",
                                  }}
                                  // dangerouslySetInnerHTML={{
                                  //   __html: formatBoldText(item.text),
                                  // }}
                                >
                                  {formatBoldText(item.text)}
                                </span>
                              )} */}
                            </ul>
                            {/* {isStreaming && (
                              <div className="streaming-placeholder">...</div>
                            )} */}
                          </div>
                        </div>

                        {/* Vulnerabilities Block - hidden for 2nd API */}
                        {!item.isSecondApi && (
                          <div className="col-md-12">
                            <div
                              className="card_block negative mb-2"
                              style={{ borderLeft: "1px solid #B91C1C29" }}
                            >
                              <h6 className="head negative_head">
                                {competitorName ?? "Tailored"} Vulnerabilities
                              </h6>
                              <ul className="list_card_ul">
                                {item.blocks?.vulnerabilities?.length ? (
                                  item.blocks.vulnerabilities.map((txt, i) => (
                                    <li key={i} className="list_card_li">
                                      <span className="list_card_text">
                                        {txt}
                                      </span>
                                    </li>
                                  ))
                                ) : (
                                  <li className="list_card_li">
                                    <span className="list_card_text">
                                      No vulnerabilities
                                    </span>
                                  </li>
                                )}
                              </ul>
                            </div>
                          </div>
                        )}

                        {/* Implications Block - hidden for 2nd API */}
                        {!item.isSecondApi && (
                          <div className="col-md-12">
                            <div
                              className="card_block neutral mb-2"
                              style={{ borderLeft: "1px solid #2563EB29" }}
                            >
                              <h6 className="head neutral_head">
                                Strategic Implications for {clientName ?? ""}
                              </h6>
                              <ul className="list_card_ul">
                                {item.blocks?.implications?.length ? (
                                  item.blocks.implications.map((txt, i) => (
                                    <li key={i} className="list_card_li">
                                      <span className="list_card_text">
                                        {txt}
                                      </span>
                                    </li>
                                  ))
                                ) : (
                                  <li className="list_card_li">
                                    <span className="list_card_text">
                                      No implications
                                    </span>
                                  </li>
                                )}
                              </ul>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="clearfix"></div>
              </>
            )
          )}
        </div>
      </div>

      {isStreaming && <div className="streaming-placeholder">...</div>}

      {/* Input Box */}
      <div className="chat_bottom_block">
        <h5 className="chat_bottom_head">Ask Your Own Question:</h5>
        <div className="chat_message_input_box">
          <input
            className="message-input-field"
            type="text"
            placeholder={`Ask anything about ${
              competitorName ?? "Tailored Chemical Products"
            } strategy, vulnerabilities, or competitive positioning...`}
            maxLength="100"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyPress}
          />
          <div className="send-message-button" onClick={handleSend}>
            <img src="/img/send_chat.png" alt="icon" className="send_icon" />
          </div>
        </div>
      </div>
    </div>
  );
}
